
// Core Contexts (Refreshed Extension Resolution)
import { AuthProvider } from '@core/context/AuthProvider';
import { useAuth } from '@core/context/AuthContext';
import { InventoryProvider } from '@core/context/InventoryProvider';
import { NotificationProvider } from '@core/context/NotificationContext';
import { UIProvider } from '@core/context/UIContext';

// Core Layouts
import AuthLayout from '@core/ui/layout/AuthLayout';
import MainLayout from '@core/ui/layout/MainLayout';
import ProtectedRoute from '@core/ui/layout/ProtectedRoute';
import PublicRoute from '@core/ui/layout/PublicRoute';
import { LoadingOverlay } from '@core/ui/StatusFeedback';
import { LazyMotion, domAnimation } from "framer-motion";
import React, { Suspense, lazy } from "react";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';

import { appConfig } from './core/config/appConfig';

// Modular Pages (Lazy Loading for Minimal Expression)
const LoginPage = lazy(() => import('./modules/auth/pages/LoginPage'));
const ReportsAdmin = lazy(() => import('./modules/admin/pages/ReportsAdmin'));
const MovementHistory = lazy(() => import('./modules/admin/pages/MovementHistory'));
const CatalogsAdmin = lazy(() => import('./modules/admin/pages/CatalogsAdmin'));
const UsersAdmin = lazy(() => import('./modules/admin/pages/UsersAdmin'));
const WithdrawalPage = lazy(() => import('./modules/withdrawal/pages/WithdrawalPage'));

const RootRoutes = () => {
    const { user, loading, isTerminalMode } = useAuth();

    if (loading) {
        return <LoadingOverlay title="Syncing" subtitle="Authenticating Security Protocol..." />;
    }
    
    return (
        <Suspense fallback={<LoadingOverlay title="Establishing" subtitle="Connecting to Industrial Network..." />}>
            <Routes>
                {/* Authentication Route (Public - Only for unauthenticated users) */}
                <Route element={<PublicRoute><AuthLayout /></PublicRoute>}>
                    <Route path="/login" element={<LoginPage />} />
                </Route>

                {/* Protected System Routes (Any Role) */}
                <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                    {/* Withdrawal Terminal (Accessible to all members) */}
                    <Route path="/terminal" element={<WithdrawalPage />} />
                    <Route path="/catalogs" element={
                        <ProtectedRoute allowedRoles={['ADMIN']}>
                            <CatalogsAdmin />
                        </ProtectedRoute>
                    } />

                    <Route path="/reports" element={
                        <ProtectedRoute allowedRoles={['ADMIN']}>
                            <ReportsAdmin />
                        </ProtectedRoute>
                    } />

                    <Route path="/audit" element={
                        <ProtectedRoute allowedRoles={['ADMIN']}>
                            <MovementHistory />
                        </ProtectedRoute>
                    } />

                    <Route path="/team" element={
                        <ProtectedRoute allowedRoles={['ADMIN']}>
                            <UsersAdmin />
                        </ProtectedRoute>
                    } />

                    <Route index element={
                        !user ? <Navigate to="/login" replace /> :
                        isTerminalMode ? <Navigate to="/terminal" replace /> :
                        user.role === 'ADMIN' ? <Navigate to="/catalogs" replace /> : 
                        <Navigate to="/terminal" replace />
                    } />
                </Route>

                {/* Default Redirection */}
                <Route path="*" element={
                    !user ? <Navigate to="/login" replace /> :
                    isTerminalMode ? <Navigate to="/terminal" replace /> :
                    user.role === 'ADMIN' ? <Navigate to="/catalogs" replace /> : 
                    <Navigate to="/terminal" replace />
                } />
            </Routes>
        </Suspense>
    );
};

function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <UIProvider>
                    <InventoryProvider>
                        <LazyMotion features={domAnimation}>
                        <NotificationProvider>
                            <Toaster
                                position={appConfig.toast.position}
                                toastOptions={appConfig.toast}
                            />
                            <RootRoutes />
                    </NotificationProvider>
                    </LazyMotion>
                </InventoryProvider>
            </UIProvider>
        </AuthProvider>
    </HelmetProvider>
    );
}

export default App;